import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Save, ShieldAlert, Sparkles, Loader2 } from "lucide-react";

/**
 * Procurement Settings — global values that shape approvals, outlet list,
 * and other procurement-wide behaviour.
 */
export default function SettingsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff/settings");
        setSettings(data);
      } catch { toast.error("Could not load settings"); }
    })();
    if (isAdmin) {
      api.get("/staff/audit-log", { params: { limit: 25 } })
        .then((r) => setAudit(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await api.put("/staff/settings", settings);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-6 text-sm text-[hsl(var(--ink-soft))]">Loading…</div>;

  return (
    <div data-testid="settings-tab">
      <div className="max-w-2xl">
        <div className="eyebrow">Procurement Settings</div>
        <h2 className="mt-1 display text-2xl">Global thresholds &amp; outlets</h2>

        {!isAdmin && (
          <div className="mt-4 flex items-center gap-2 text-xs text-[hsl(var(--ink-soft))] p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
            <ShieldAlert size={14} /> Read-only view — only admins can edit these values.
          </div>
        )}

        <div className="mt-6 space-y-5">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Approval threshold (₹)
            </span>
            <input
              type="number" step="1" min="0"
              data-testid="settings-threshold"
              disabled={!isAdmin}
              value={settings.approval_threshold_inr}
              onChange={(e) => setSettings({ ...settings, approval_threshold_inr: Number(e.target.value) })}
              className="tbb-inp mt-1 w-40"
            />
            <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
              POs above this amount require admin approval. Outlet managers can approve POs at or below this value.
            </p>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Default outlet</span>
            <select
              disabled={!isAdmin}
              data-testid="settings-default-outlet"
              value={settings.default_outlet}
              onChange={(e) => setSettings({ ...settings, default_outlet: e.target.value })}
              className="tbb-inp mt-1 w-64"
            >
              {(settings.outlets || []).map((o) => (
                <option key={o.code} value={o.code}>{o.name}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                CTRL-02 line tolerance %
              </span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="settings-line-tol"
                disabled={!isAdmin}
                value={settings.ctrl02_line_tolerance_pct ?? 10}
                onChange={(e) => setSettings({ ...settings, ctrl02_line_tolerance_pct: Number(e.target.value) })}
                className="tbb-inp mt-1 w-40"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                Blocks GRN approval if cumulative line receipt exceeds ordered qty by more than this %. Spec default: 10%.
              </p>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                CTRL-02 header tolerance %
              </span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="settings-header-tol"
                disabled={!isAdmin}
                value={settings.ctrl02_header_tolerance_pct ?? 5}
                onChange={(e) => setSettings({ ...settings, ctrl02_header_tolerance_pct: Number(e.target.value) })}
                className="tbb-inp mt-1 w-40"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                Blocks GRN approval if cumulative GRN value exceeds PO total by more than this %. Spec default: 5%. Raise to 10-15% if vendor drift routinely eats headroom.
              </p>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                GRN rate deviation · amber %
              </span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="settings-dev-amber"
                disabled={!isAdmin}
                value={settings.grn_rate_deviation_amber_pct ?? 5}
                onChange={(e) => setSettings({ ...settings, grn_rate_deviation_amber_pct: Number(e.target.value) })}
                className="tbb-inp mt-1 w-40"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                Highlights a GRN line amber when the entered rate differs from the PO rate by more than this %. Default 5%.
              </p>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                GRN rate deviation · red %
              </span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="settings-dev-red"
                disabled={!isAdmin}
                value={settings.grn_rate_deviation_red_pct ?? 15}
                onChange={(e) => setSettings({ ...settings, grn_rate_deviation_red_pct: Number(e.target.value) })}
                className="tbb-inp mt-1 w-40"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
                Escalates the deviation badge to red when the drift crosses this %. Default 15%.
              </p>
            </label>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-2">Outlets</div>
            <ul className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))]">
              {(settings.outlets || []).map((o) => (
                <li key={o.code} className="p-2 text-sm flex items-center gap-3">
                  <span className="font-mono text-[10px] text-[hsl(var(--brass-2))]">{o.code}</span>
                  <span>{o.name}</span>
                </li>
              ))}
            </ul>
          </div>

          {isAdmin && (
            <div className="pt-2 flex flex-wrap gap-3 items-center">
              <button data-testid="settings-save" onClick={save} disabled={saving}
                      className="btn-brass !py-2.5 !px-5 text-sm inline-flex items-center gap-2">
                <Save size={14} /> {saving ? "Saving…" : "Save settings"}
              </button>
              <SeedRateCardsButton />
            </div>
          )}
        </div>

        {isAdmin && audit.length > 0 && (
          <div className="mt-10">
            <div className="eyebrow mb-2">Audit log · latest 25</div>
            <div className="border border-[hsl(var(--rule))]">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
                  <tr><th className="text-left p-2">When</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Entity</th><th className="text-left p-2">By</th></tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--rule))]">
                  {audit.map((a, i) => (
                    <tr key={i} data-testid={`audit-row-${i}`}>
                      <td className="p-2 text-[hsl(var(--ink-soft))]">{new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-2">{a.kind}</td>
                      <td className="p-2 font-mono">{a.entity}:{a.entity_id.slice(0, 8)}</td>
                      <td className="p-2 text-[hsl(var(--ink-soft))]">{a.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}

function SeedRateCardsButton() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/vendor-rate-cards/seed-from-history",
        { days: 90, dry_run: true, min_observations: 2 });
      setPreview(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!preview || !preview.proposed_count) return;
    if (!window.confirm(`Create ${preview.proposed_count} rate card${preview.proposed_count === 1 ? "" : "s"} from history? Existing cards are never overwritten.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/vendor-rate-cards/seed-from-history",
        { days: 90, dry_run: false, min_observations: 2 });
      toast.success(`Created ${data.created_count} rate card${data.created_count === 1 ? "" : "s"}`);
      setPreview(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Seed failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <button
        data-testid="settings-seed-rate-cards"
        onClick={runDryRun}
        disabled={busy}
        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))]"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
        {preview ? "Re-run preview" : "Seed rate cards from history"}
      </button>
      {preview && (
        <div className="text-xs text-[hsl(var(--ink-soft))]" data-testid="seed-preview">
          <div>Proposed <b>{preview.proposed_count}</b> new cards · skipping <b>{preview.skipped_existing}</b> with active card, <b>{preview.skipped_low_observations}</b> with too few observations.</div>
          {preview.proposed_count > 0 && (
            <button
              data-testid="settings-seed-rate-cards-apply"
              onClick={apply}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded btn-brass"
            >
              Apply {preview.proposed_count} cards
            </button>
          )}
        </div>
      )}
    </div>
  );
}
